import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Modal, Form, Button, Row, Col, Badge } from 'react-bootstrap';
import { colors } from '../brand-colors';
import authService from '../services/authService';
import analyticsService from '../services/analyticsService';

// Header height constant
const HEADER_HEIGHT = 56;

/**
 * Command Center Layout with collapsible sidebar
 */
function CommandCenterLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // User menu and settings modal state
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showPrepTasksModal, setShowPrepTasksModal] = useState(false);
  const [prepTaskTemplates, setPrepTaskTemplates] = useState([]);
  const [prepTasksLoading, setPrepTasksLoading] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDays, setNewTaskDays] = useState(7);
  const [editingTask, setEditingTask] = useState(null);
  const [profileData, setProfileData] = useState({
    owner_name: '',
    business_name: '',
    industry: '',
    website: '',
    sales_page: ''
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [preferences, setPreferences] = useState({
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
    reminder_hour: '9',
    reminder_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    quiet_mode: false,
    reminder_email: true,
    reminder_sms: false,
  });
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsSaved, setPrefsSaved] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [criticalComplete, setCriticalComplete] = useState(true); // default true so menu isn't locked on load

  useEffect(() => {
    checkAuth();
    fetchPreferences();
    // Check if onboarding is fully complete + critical items done
    const checkOnboarding = async () => {
      try {
        const response = await fetch('/api/onboarding/status', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setOnboardingComplete(data.ready_to_proceed === true);
          setCriticalComplete(!data.priority_counts || data.priority_counts.critical === 0);
        }
      } catch (err) {
        // Silently fail
      }
    };
    checkOnboarding();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await fetch('/api/preferences', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.preferences) {
          setPreferences(prev => ({
            ...prev,
            timezone: data.preferences.timezone || prev.timezone,
            reminder_hour: data.preferences.reminder_hour || '9',
            reminder_days: data.preferences.reminder_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            quiet_mode: data.preferences.quiet_mode || false,
            reminder_email: data.preferences.reminder_email !== false,
            reminder_sms: data.preferences.reminder_sms || false,
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching preferences:', err);
    }
  };

  const savePreferences = async () => {
    setPrefsSaving(true);
    setPrefsSaved(false);
    try {
      const response = await fetch('/api/preferences', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences)
      });
      if (response.ok) {
        setPrefsSaved(true);
        setTimeout(() => setPrefsSaved(false), 3000);
      }
    } catch (err) {
      console.error('Error saving preferences:', err);
    } finally {
      setPrefsSaving(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await fetch('/api/authority-engineer/audit/profile-preview', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.profile) {
          setProfileData({
            owner_name: data.profile.name || '',
            business_name: data.profile.business || '',
            industry: data.profile.industry || '',
            website: data.profile.website || '',
            sales_page: data.profile.sales_page || ''
          });
        }
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    setProfileSaved(false);
    try {
      // Save each field individually using the business data API
      const fields = ['owner_name', 'business_name', 'industry', 'website', 'sales_page'];
      for (const field of fields) {
        if (profileData[field] !== undefined) {
          await fetch('/api/business/field', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ field_name: field, field_value: profileData[field] })
          });
        }
      }
      setProfileSaved(true);
      setTimeout(() => {
        setProfileSaved(false);
        setShowAccountModal(false);
      }, 1000);
    } catch (err) {
      console.error('Error saving profile:', err);
    } finally {
      setProfileSaving(false);
    }
  };

  // Prep Task Template functions
  const fetchPrepTaskTemplates = async () => {
    setPrepTasksLoading(true);
    try {
      const response = await fetch('/api/prep-task-templates', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Add fallbacks for older data that might not have task_type
          const templates = (data.templates || []).map(t => ({
            ...t,
            task_type: t.task_type || 'human',
            is_optional: t.is_optional ?? true
          }));
          setPrepTaskTemplates(templates);
        }
      }
    } catch (err) {
      console.error('Error fetching prep task templates:', err);
    } finally {
      setPrepTasksLoading(false);
    }
  };

  const togglePrepTask = async (taskId) => {
    try {
      const response = await fetch(`/api/prep-task-templates/toggle/${taskId}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setPrepTaskTemplates(prev => prev.map(t =>
          t.id === taskId ? { ...t, is_active: data.is_active } : t
        ));
      }
    } catch (err) {
      console.error('Error toggling prep task:', err);
    }
  };

  const addPrepTask = async () => {
    if (!newTaskTitle.trim()) return;
    try {
      const response = await fetch('/api/prep-task-templates', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newTaskTitle,
          days_before: newTaskDays,
          category: 'custom',
          is_active: true
        })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPrepTaskTemplates(prev => [...prev, data.template]);
          setNewTaskTitle('');
          setNewTaskDays(7);
        }
      }
    } catch (err) {
      console.error('Error adding prep task:', err);
    }
  };

  const updatePrepTask = async (taskId, updates) => {
    try {
      const response = await fetch(`/api/prep-task-templates/${taskId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPrepTaskTemplates(prev => prev.map(t =>
            t.id === taskId ? { ...t, ...data.template } : t
          ));
          setEditingTask(null);
        }
      }
    } catch (err) {
      console.error('Error updating prep task:', err);
    }
  };

  const deletePrepTask = async (taskId) => {
    if (!window.confirm('Delete this task template?')) return;
    try {
      const response = await fetch(`/api/prep-task-templates/${taskId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        setPrepTaskTemplates(prev => prev.filter(t => t.id !== taskId));
      }
    } catch (err) {
      console.error('Error deleting prep task:', err);
    }
  };

  const resetPrepTasksToDefaults = async () => {
    if (!window.confirm('Reset all prep tasks to defaults? This will delete any custom tasks.')) return;
    try {
      const response = await fetch('/api/prep-task-templates/reset', {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        fetchPrepTaskTemplates();
      }
    } catch (err) {
      console.error('Error resetting prep tasks:', err);
    }
  };

  const checkAuth = async () => {
    const result = await authService.checkAuth();
    if (result.authenticated) {
      setUser(result.user);
      if (!result.user.command_center_tier) {
        navigate('/dashboard/marketing-strategy');
      }
    } else {
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    analyticsService.auth.logout();
    await authService.logout();
    navigate('/login');
  };

  const userEmail = user?.email || '';

  const allMenuItems = [
    // Daily drivers
    { path: '/command-center', label: 'Dashboard', icon: '📊', exact: true, alwaysShow: true },
    ...(!onboardingComplete ? [{ path: '/command-center/onboarding', label: 'Onboarding Status', icon: '📋', alwaysShow: true }] : []),
    { path: '/command-center/campaign-hq', label: 'Campaign HQ', icon: '🎯' },
    { path: '/command-center/market-intelligence', label: 'Market Intelligence', icon: '📡' },
    { path: '/command-center/war-room', label: 'War Room', icon: '🖥️' },
    { divider: true },
    // Engineers — workbenches
    { path: '/command-center/authority-engineer', label: 'Authority Engineer', icon: '🧠' },
    { path: '/command-center/brand-engineer', label: 'Brand Engineer', icon: '🎨' },
    { path: '/command-center/business-engineer', label: 'Business Engineer', icon: '🤖' },
    { path: '/command-center/financial-engineer', label: 'Financial Engineer', icon: '💰' },
    { divider: true },
    // Reference
    { path: '/command-center/products', label: 'Products', icon: '📦' },
    { path: '/command-center/calendars', label: 'Calendars & Tasks', icon: '📅' },
  ];

  // Lock menu to Dashboard + Onboarding until critical items are done
  const menuItems = !criticalComplete
    ? allMenuItems.filter(item => item.alwaysShow)
    : allMenuItems;

  // Styles
  const layoutStyle = {
    display: 'flex',
    minHeight: '100vh',
  };

  const sidebarStyle = {
    width: sidebarCollapsed ? '60px' : '260px',
    backgroundColor: colors.greyDarkest,
    borderRight: `2px solid ${colors.goldDark}`,
    transition: 'width 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: HEADER_HEIGHT,
    left: 0,
    height: `calc(100vh - ${HEADER_HEIGHT}px)`,
    zIndex: 1000,
    overflow: 'hidden',
  };

  const sidebarHeaderStyle = {
    padding: '0.75rem',
    borderBottom: `1px solid ${colors.goldDark}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50px',
  };

  const collapseButtonStyle = {
    background: 'none',
    border: 'none',
    color: colors.goldLight,
    cursor: 'pointer',
    padding: '0.5rem',
    fontSize: '1.2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const menuStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: '0.5rem 0',
  };

  const menuItemStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: sidebarCollapsed ? '0.75rem' : '0.75rem 1.25rem',
    color: colors.greyLight,
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    borderLeft: '3px solid transparent',
    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
  };

  const menuItemMutedStyle = {
    opacity: 0.4,
    pointerEvents: 'none',
  };

  const menuItemActiveStyle = {
    backgroundColor: colors.greyDarker,
    color: colors.goldLight,
    borderLeftColor: colors.goldLight,
  };

  const menuItemHoverStyle = {
    backgroundColor: colors.greyDark,
    color: colors.goldLight,
  };

  const iconStyle = {
    fontSize: '1.2rem',
    marginRight: sidebarCollapsed ? 0 : '0.75rem',
    flexShrink: 0,
  };

  const labelStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    opacity: sidebarCollapsed ? 0 : 1,
    transition: 'opacity 0.2s ease',
  };

  const userSectionStyle = {
    padding: sidebarCollapsed ? '1rem 0.5rem' : '1rem 1.25rem',
    borderTop: `1px solid ${colors.goldDark}`,
  };

  const userAvatarStyle = {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: colors.goldLight,
    color: colors.greyDarkest,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600',
    cursor: 'pointer',
    margin: sidebarCollapsed ? '0 auto' : 0,
  };

  const mainContentStyle = {
    flex: 1,
    marginLeft: sidebarCollapsed ? '60px' : '260px',
    marginTop: HEADER_HEIGHT,
    transition: 'margin-left 0.3s ease',
    backgroundColor: '#f8f9fa',
    minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
  };

  // Header styles
  const headerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: HEADER_HEIGHT,
    backgroundColor: colors.greyDarkest,
    borderBottom: `2px solid ${colors.goldDark}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 1.5rem',
    zIndex: 1001,
  };

  const headerLogoStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  };

  const headerTitleStyle = {
    color: colors.goldLight,
    fontSize: '1.25rem',
    fontWeight: '600',
    fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
    letterSpacing: '0.15em',
  };

  // Get current page title from path
  const getPageTitle = () => {
    const path = location.pathname;
    const menuItem = menuItems.find(item =>
      item.exact ? path === item.path : path.startsWith(item.path) && item.path !== '/command-center'
    );
    if (menuItem) return menuItem.label.toUpperCase();
    if (path === '/command-center' || path === '/command-center/') return 'DASHBOARD';
    return 'COMMAND CENTER';
  };

  const getInitials = (email) => {
    if (!email) return '?';
    return email.charAt(0).toUpperCase();
  };

  return (
    <div style={layoutStyle}>
      {/* Top Header Bar */}
      <header style={headerStyle}>
        <div style={headerLogoStyle}>
          <img
            src="/images/logo-gold.png"
            alt="AI Profit Engineer"
            style={{ height: '36px', width: 'auto' }}
          />
          <span style={headerTitleStyle}>COMMAND CENTER</span>
        </div>
        <span style={headerTitleStyle}>{getPageTitle()}</span>
      </header>

      {/* Sidebar */}
      <aside style={sidebarStyle}>
        {/* Sidebar Toggle */}
        <div style={sidebarHeaderStyle}>
          <button
            style={collapseButtonStyle}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>
        </div>

        {/* Menu Items */}
        <nav style={menuStyle}>
          {menuItems.map((item, idx) => (
            item.divider ? (
              <div key={`divider-${idx}`} style={{
                borderTop: `1px solid ${colors.goldDark}`,
                margin: sidebarCollapsed ? '0.5rem 0.5rem' : '0.5rem 1rem',
                opacity: 0.4,
              }} />
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                style={({ isActive }) => ({
                  ...menuItemStyle,
                  ...(isActive ? menuItemActiveStyle : {}),
                  ...(item.muted ? menuItemMutedStyle : {}),
                })}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.classList.contains('active') && !item.muted) {
                    Object.assign(e.currentTarget.style, menuItemHoverStyle);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.classList.contains('active') && !item.muted) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = colors.greyLight;
                  }
                }}
                title={sidebarCollapsed ? item.label : (item.muted ? 'Coming soon' : '')}
              >
                <span style={iconStyle}>{item.icon}</span>
                <span style={labelStyle}>{item.label}</span>
              </NavLink>
            )
          ))}
        </nav>

        {/* Profit Map Link */}
        <div
          style={{
            padding: sidebarCollapsed ? '0.75rem' : '0.75rem 1.25rem',
            borderTop: `1px solid ${colors.goldDark}`,
          }}
        >
          <NavLink
            to="/dashboard"
            style={{
              display: 'flex',
              alignItems: 'center',
              color: colors.goldLight,
              textDecoration: 'none',
              fontSize: '0.85rem',
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.goldLight}
            title="Back to Profit Map"
          >
            <span style={{ fontSize: '1.1rem', marginRight: sidebarCollapsed ? 0 : '0.5rem' }}>←</span>
            {!sidebarCollapsed && <span>Profit Map</span>}
          </NavLink>
        </div>

        {/* User Section */}
        <div style={{ ...userSectionStyle, position: 'relative' }}>
          <div
            style={userAvatarStyle}
            onClick={() => setShowUserMenu(!showUserMenu)}
            title={`${userEmail || 'User'} - Click for menu`}
          >
            {getInitials(userEmail)}
          </div>
          {!sidebarCollapsed && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: colors.greyMid, wordBreak: 'break-all' }}>
              {userEmail}
            </div>
          )}

          {/* User Dropdown Menu */}
          {showUserMenu && (
            <>
              {/* Backdrop to close menu when clicking outside */}
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1001,
                }}
                onClick={() => setShowUserMenu(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: sidebarCollapsed ? '50%' : '0',
                  transform: sidebarCollapsed ? 'translateX(-50%)' : 'none',
                  marginBottom: '8px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                  minWidth: '240px',
                  zIndex: 1002,
                  overflow: 'hidden',
                }}
              >
                {/* User Info Header */}
                <div style={{ padding: '1rem', borderBottom: '1px solid #eee', backgroundColor: '#f8f9fa' }}>
                  <div style={{ fontWeight: '600', color: colors.greyDarkest, marginBottom: '2px' }}>
                    {userEmail}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: colors.greyMid }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      backgroundColor: colors.goldLight,
                      color: colors.greyDarkest,
                      borderRadius: '4px',
                      fontWeight: '500',
                    }}>
                      Pro Plan
                    </span>
                  </div>
                </div>

                {/* Menu Items */}
                <div style={{ padding: '0.5rem 0' }}>
                  <div
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: colors.greyDarkest,
                      transition: 'background-color 0.15s',
                    }}
                    onClick={() => {
                      setShowUserMenu(false);
                      setShowSettingsModal(true);
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span style={{ marginRight: '0.75rem' }}>🔔</span>
                    Reminder Settings
                  </div>

                  <div
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: colors.greyDarkest,
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    onClick={() => { setShowUserMenu(false); fetchProfile(); setShowAccountModal(true); }}
                  >
                    <span style={{ marginRight: '0.75rem' }}>👤</span>
                    Account Settings
                  </div>

                  <div
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: colors.greyDarkest,
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    onClick={() => { setShowUserMenu(false); fetchPrepTaskTemplates(); setShowPrepTasksModal(true); }}
                  >
                    <span style={{ marginRight: '0.75rem' }}>📋</span>
                    Promotion Prep Tasks
                  </div>

                  <div
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: colors.greyDarkest,
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    onClick={() => { setShowUserMenu(false); navigate('/command-center/settings'); }}
                  >
                    <span style={{ marginRight: '0.75rem' }}>🔌</span>
                    WordPress & Plugin
                  </div>

                  <div
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: colors.greyMid,
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    title="Coming soon"
                  >
                    <span style={{ marginRight: '0.75rem' }}>💳</span>
                    Subscription & Billing
                    <span style={{ marginLeft: 'auto', fontSize: '0.7rem', opacity: 0.6 }}>Soon</span>
                  </div>
                </div>

                {/* Logout */}
                <div style={{ borderTop: '1px solid #eee', padding: '0.5rem 0' }}>
                  <div
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#dc3545',
                      transition: 'background-color 0.15s',
                    }}
                    onClick={() => {
                      setShowUserMenu(false);
                      handleLogout();
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fff5f5'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span style={{ marginRight: '0.75rem' }}>🚪</span>
                    Logout
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main style={mainContentStyle}>
        {!criticalComplete
          && location.pathname !== '/command-center'
          && location.pathname !== '/command-center/onboarding'
          ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>
              <h3 style={{ color: colors.greyDarkest, marginBottom: '1rem' }}>Complete Critical Setup First</h3>
              <p style={{ color: colors.greyMid, marginBottom: '1.5rem' }}>
                You need to finish your critical onboarding items before accessing this area.
              </p>
              <Button
                variant="dark"
                onClick={() => navigate('/command-center/onboarding')}
                style={{ backgroundColor: colors.greyDarkest }}
              >
                Go to Onboarding
              </Button>
            </div>
          ) : (
            <Outlet />
          )}
      </main>

      {/* Settings Modal */}
      <Modal show={showSettingsModal} onHide={() => setShowSettingsModal(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>
            <span className="me-2">🔔</span>
            Reminder Settings
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            Configure when and how you receive reminders about tasks and deadlines.
          </p>
          <h6 className="mb-3" style={{ color: colors.greyDarkest }}>
            Timing
            {preferences.quiet_mode && (
              <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7rem' }}>
                Quiet Mode On
              </Badge>
            )}
          </h6>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Timezone</Form.Label>
                <Form.Select
                  value={preferences.timezone}
                  onChange={(e) => setPreferences({ ...preferences, timezone: e.target.value })}
                >
                  <optgroup label="United States">
                    <option value="America/New_York">Eastern Time (New York)</option>
                    <option value="America/Chicago">Central Time (Chicago)</option>
                    <option value="America/Denver">Mountain Time (Denver)</option>
                    <option value="America/Phoenix">Arizona (Phoenix - No DST)</option>
                    <option value="America/Los_Angeles">Pacific Time (Los Angeles)</option>
                    <option value="America/Anchorage">Alaska (Anchorage)</option>
                    <option value="Pacific/Honolulu">Hawaii (Honolulu)</option>
                  </optgroup>
                  <optgroup label="Canada">
                    <option value="America/Toronto">Eastern (Toronto)</option>
                    <option value="America/Vancouver">Pacific (Vancouver)</option>
                  </optgroup>
                  <optgroup label="Europe">
                    <option value="Europe/London">UK (London)</option>
                    <option value="Europe/Paris">Central Europe (Paris)</option>
                    <option value="Europe/Berlin">Central Europe (Berlin)</option>
                  </optgroup>
                  <optgroup label="Australia">
                    <option value="Australia/Sydney">Eastern (Sydney)</option>
                    <option value="Australia/Perth">Western (Perth)</option>
                  </optgroup>
                  <optgroup label="Other">
                    <option value="UTC">UTC</option>
                  </optgroup>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Preferred Reminder Time</Form.Label>
                <Form.Select
                  value={preferences.reminder_hour}
                  onChange={(e) => setPreferences({ ...preferences, reminder_hour: e.target.value })}
                >
                  <option value="7">7:00 AM</option>
                  <option value="8">8:00 AM</option>
                  <option value="9">9:00 AM</option>
                  <option value="10">10:00 AM</option>
                  <option value="11">11:00 AM</option>
                  <option value="12">12:00 PM</option>
                  <option value="13">1:00 PM</option>
                  <option value="14">2:00 PM</option>
                  <option value="15">3:00 PM</option>
                  <option value="16">4:00 PM</option>
                  <option value="17">5:00 PM</option>
                  <option value="18">6:00 PM</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Which days can we send reminders?</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                    <Form.Check
                      key={day}
                      type="checkbox"
                      id={`settings-day-${day}`}
                      label={day.charAt(0).toUpperCase() + day.slice(1, 3)}
                      checked={preferences.reminder_days.includes(day)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setPreferences({ ...preferences, reminder_days: [...preferences.reminder_days, day] });
                        } else {
                          setPreferences({ ...preferences, reminder_days: preferences.reminder_days.filter(d => d !== day) });
                        }
                      }}
                      inline
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col md={6}>
              <Form.Check
                type="switch"
                id="settings-quiet-mode"
                label="Quiet Mode (pause all reminders)"
                checked={preferences.quiet_mode}
                onChange={(e) => setPreferences({ ...preferences, quiet_mode: e.target.checked })}
              />
              <Form.Text className="text-muted">
                Going on vacation? Turn this on to pause reminders.
              </Form.Text>
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="settings-email-reminders"
                label="Email Reminders"
                checked={preferences.reminder_email}
                onChange={(e) => setPreferences({ ...preferences, reminder_email: e.target.checked })}
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="settings-sms-reminders"
                label="SMS Reminders"
                checked={preferences.reminder_sms}
                onChange={(e) => setPreferences({ ...preferences, reminder_sms: e.target.checked })}
                disabled
              />
              <Form.Text className="text-muted">SMS coming soon</Form.Text>
            </Col>
          </Row>

        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSettingsModal(false)}>
            Cancel
          </Button>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={savePreferences}
            disabled={prefsSaving}
          >
            {prefsSaving ? 'Saving...' : prefsSaved ? '✓ Saved!' : 'Save Settings'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Account Settings Modal */}
      <Modal show={showAccountModal} onHide={() => setShowAccountModal(false)}>
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>
            <span className="me-2">👤</span>
            Account Settings
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            Update your profile information. This is used across the platform including for Authority Engineer AI visibility checks.
          </p>

          <Form.Group className="mb-3">
            <Form.Label>Your Name</Form.Label>
            <Form.Control
              type="text"
              value={profileData.owner_name}
              onChange={(e) => setProfileData({ ...profileData, owner_name: e.target.value })}
              placeholder="e.g., John Smith"
            />
            <Form.Text className="text-muted">
              Used for AI visibility searches and personalization
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Business Name</Form.Label>
            <Form.Control
              type="text"
              value={profileData.business_name}
              onChange={(e) => setProfileData({ ...profileData, business_name: e.target.value })}
              placeholder="e.g., Smith Consulting LLC"
            />
            <Form.Text className="text-muted">
              Your company or brand name
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Industry</Form.Label>
            <Form.Control
              type="text"
              value={profileData.industry}
              onChange={(e) => setProfileData({ ...profileData, industry: e.target.value })}
              placeholder="e.g., Business Consulting, Marketing, etc."
            />
            <Form.Text className="text-muted">
              Your primary industry or niche
            </Form.Text>
          </Form.Group>

          <hr className="my-4" />
          <h6 className="text-muted mb-3">Web Presence</h6>

          <Form.Group className="mb-3">
            <Form.Label>Website URL</Form.Label>
            <Form.Control
              type="url"
              value={profileData.website}
              onChange={(e) => setProfileData({ ...profileData, website: e.target.value })}
              placeholder="e.g., https://yourwebsite.com"
            />
            <Form.Text className="text-muted">
              Your main website - used to auto-scan for IP and authority signals
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Sales Page URL <span className="text-muted">(optional)</span></Form.Label>
            <Form.Control
              type="url"
              value={profileData.sales_page}
              onChange={(e) => setProfileData({ ...profileData, sales_page: e.target.value })}
              placeholder="e.g., https://yourwebsite.com/offer"
            />
            <Form.Text className="text-muted">
              Your primary sales or offer page - helps with product analysis
            </Form.Text>
          </Form.Group>

        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAccountModal(false)}>
            Cancel
          </Button>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={saveProfile}
            disabled={profileSaving}
          >
            {profileSaving ? 'Saving...' : profileSaved ? '✓ Saved!' : 'Save Profile'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Promotion Prep Tasks Modal */}
      <Modal show={showPrepTasksModal} onHide={() => { setShowPrepTasksModal(false); setEditingTask(null); }} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>
            <span className="me-2">📋</span>
            Promotion Prep Tasks
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            These tasks are automatically added to your calendar when you create a promotion with "Add prep tasks to calendar" enabled.
            Toggle tasks on/off to customize your default checklist.
          </p>

          {prepTasksLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-secondary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Group tasks by type */}
              {[
                { type: 'human', label: '📋 Your Tasks (Calendar)', description: 'These tasks are added to your calendar when you create a promotion.' },
                { type: 'aipe_required', label: '🤖 AI Profit Engineer Automated (Required)', description: 'The software handles these automatically - no action needed from you.' },
                { type: 'aipe_optional', label: '🎛️ AI Profit Engineer Automated (Optional)', description: 'Toggle these off if you don\'t need them for certain promotions.' }
              ].map(({ type, label, description }) => {
                const typeTasks = prepTaskTemplates.filter(t => {
                  if (type === 'human') return t.task_type === 'human';
                  if (type === 'aipe_required') return t.task_type === 'aipe' && !t.is_optional;
                  if (type === 'aipe_optional') return t.task_type === 'aipe' && t.is_optional;
                  return false;
                });
                if (typeTasks.length === 0) return null;

                const isAipe = type.startsWith('aipe');

                return (
                  <div key={type} className="mb-4">
                    <h6 style={{ color: colors.greyDarkest, borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
                      {label}
                    </h6>
                    <small className="text-muted d-block mb-2">{description}</small>
                    <div className="list-group list-group-flush">
                      {typeTasks.map(task => (
                        <div
                          key={task.id}
                          className="list-group-item d-flex align-items-center justify-content-between py-2 px-0"
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderBottom: '1px solid #f0f0f0',
                            opacity: isAipe ? 0.7 : 1
                          }}
                        >
                          {editingTask?.id === task.id ? (
                            // Edit mode
                            <div className="d-flex align-items-center gap-2 flex-grow-1">
                              <Form.Control
                                type="text"
                                size="sm"
                                value={editingTask.title}
                                onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                                style={{ maxWidth: '400px' }}
                              />
                              <Form.Control
                                type="number"
                                size="sm"
                                value={editingTask.days_before}
                                onChange={(e) => setEditingTask({ ...editingTask, days_before: parseInt(e.target.value) || 0 })}
                                style={{ width: '80px' }}
                                min="-7"
                              />
                              <span className="text-muted" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                {editingTask.days_before >= 0 ? 'days before' : 'days after'}
                              </span>
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => updatePrepTask(task.id, { title: editingTask.title, days_before: editingTask.days_before })}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => setEditingTask(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            // View mode
                            <>
                              <div className="d-flex align-items-center">
                                {task.is_optional ? (
                                  <Form.Check
                                    type="switch"
                                    id={`task-${task.id}`}
                                    checked={task.is_active}
                                    onChange={() => togglePrepTask(task.id)}
                                    className="me-2"
                                  />
                                ) : (
                                  <span className="me-3" style={{ width: '36px', display: 'inline-block', textAlign: 'center' }}>
                                    {isAipe ? '🔒' : '✓'}
                                  </span>
                                )}
                                <span style={{
                                  color: task.is_active ? (isAipe ? colors.greyMid : colors.greyDarkest) : colors.greyMid,
                                  textDecoration: task.is_active ? 'none' : 'line-through'
                                }}>
                                  {task.title}
                                </span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <Badge
                                  bg={task.days_before < 0 ? 'info' : 'secondary'}
                                  style={{ fontWeight: 'normal' }}
                                >
                                  {task.days_before < 0
                                    ? `${Math.abs(task.days_before)}d after`
                                    : `${task.days_before}d before`}
                                </Badge>
                                {task.is_optional && task.task_type === 'human' && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="link"
                                      className="p-0 text-muted"
                                      onClick={() => setEditingTask({ id: task.id, title: task.title, days_before: task.days_before })}
                                      title="Edit task"
                                    >
                                      ✏️
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="link"
                                      className="p-0 text-danger"
                                      onClick={() => deletePrepTask(task.id)}
                                      title="Delete task"
                                    >
                                      🗑️
                                    </Button>
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Add new task form */}
              <div className="mt-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <h6 style={{ color: colors.greyDarkest }}>Add Custom Task</h6>
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Task description..."
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addPrepTask()}
                    style={{ flex: 1 }}
                  />
                  <Form.Control
                    type="number"
                    value={newTaskDays}
                    onChange={(e) => setNewTaskDays(parseInt(e.target.value) || 0)}
                    style={{ width: '80px' }}
                    min="0"
                  />
                  <span className="text-muted" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>days before</span>
                  <Button variant="primary" onClick={addPrepTask} disabled={!newTaskTitle.trim()}>
                    Add
                  </Button>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={resetPrepTasksToDefaults}>
            Reset to Defaults
          </Button>
          <Button variant="dark" style={{ backgroundColor: colors.greyDarkest }} onClick={() => setShowPrepTasksModal(false)}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CommandCenterLayout;
